import { logError } from "../../utils/error";
import { supabase } from "../supabase/client";

export interface UserLicenseStatus {
  email: string;
  exists: boolean;
  hasEnlightenPro: boolean;
  licenses: string[];
  error?: string;
}

interface BulkResponse {
  results?: UserLicenseStatus[];
}

const FUNCTION_NAME = "check-user-license";

export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

interface InvokeError {
  message?: string;
  context?: { response?: Response };
}

const readServerErrorMessage = async (
  invokeError: InvokeError
): Promise<string | undefined> => {
  const httpResponse = invokeError.context?.response;
  if (!httpResponse) {
    return invokeError.message;
  }
  try {
    const cloned = httpResponse.clone();
    const body = await cloned.json();
    if (body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string") {
      return (body as { error: string }).error;
    }
  } catch {
    return invokeError.message;
  }
  return invokeError.message;
};

export const checkUserLicenses = async (
  emails: string[],
  tenantId: string
): Promise<UserLicenseStatus[]> => {
  if (emails.length === 0) {
    return [];
  }

  const response = await supabase.functions.invoke<BulkResponse>(FUNCTION_NAME, {
    body: {
      emails: emails.map(normalizeEmail),
      tenant_id: tenantId
    }
  });

  if (response.error) {
    const serverMessage = await readServerErrorMessage(response.error);
    logError("Error checking user licenses", { message: serverMessage });
    throw new Error(serverMessage ?? "License check failed");
  }

  return response.data?.results ?? [];
};
